import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api, formatApiError } from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Edit, RefreshCw, LogOut, Youtube, CheckCircle2, XCircle, ExternalLink, Mic } from 'lucide-react';

const emptyPost = { title: '', excerpt: '', content: '', cover_image: '', category: 'Reflections', tags: '', is_published: true };

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('posts');

  useEffect(() => {
    if (user === false) navigate('/admin/login');
  }, [user, navigate]);

  if (user === null) return <div className="pt-40 text-center text-gray-500">Checking session...</div>;
  if (!user) return null;

  return (
    <div className="pt-28 pb-16 px-4 sm:px-6 min-h-screen" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
            <p className="text-gray-400 text-sm mt-1">Welcome back, {user.name || user.email}</p>
          </div>
          <button onClick={() => { logout(); navigate('/'); }} data-testid="admin-logout" className="flex items-center gap-2 px-5 py-2.5 glass border border-white/10 rounded-full text-sm hover:border-[#EF5B5B] smooth">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>

        <div className="flex flex-wrap gap-3 mb-8 border-b border-white/10 pb-4">
          {[['posts', 'Newsletter Posts'], ['episodes', 'Episodes'], ['applications', 'Speaker Applications'], ['about', 'About Page'], ['subscribers', 'Subscribers']].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`} className={`px-5 py-2.5 rounded-full text-sm font-medium smooth ${tab === k ? 'bg-[#EF5B5B] text-white' : 'glass text-gray-300 hover:bg-white/10'}`}>{label}</button>
          ))}
        </div>

        {tab === 'posts' && <PostsManager />}
        {tab === 'episodes' && <EpisodesManager />}
        {tab === 'applications' && <ApplicationsManager />}
        {tab === 'about' && <AboutManager />}
        {tab === 'subscribers' && <SubscribersManager />}
      </div>
    </div>
  );
};

// ---------------- Posts Manager ----------------
const PostsManager = () => {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | post object
  const [form, setForm] = useState(emptyPost);
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/admin/posts').then((res) => setPosts(res.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(emptyPost); setEditing('new'); };
  const startEdit = (p) => { setForm({ ...p, tags: (p.tags || []).join(', ') }); setEditing(p); };

  const save = async () => {
    setSaving(true);
    const payload = { ...form, tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean) };
    try {
      if (editing === 'new') {
        await api.post('/posts', payload);
        toast.success('Post created');
      } else {
        await api.put(`/posts/${editing.id}`, payload);
        toast.success('Post updated');
      }
      await load();
      setEditing(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (p) => {
    if (!window.confirm('Delete this post?')) return;
    await api.delete(`/posts/${p.id}`);
    toast.success('Post deleted');
    load();
  };

  if (editing) {
    return (
      <div className="glass-strong rounded-2xl p-6 sm:p-8 max-w-3xl" data-testid="post-editor">
        <h2 className="text-xl font-bold mb-6">{editing === 'new' ? 'New Post' : 'Edit Post'}</h2>
        <div className="space-y-4">
          <Field label="Title"><input data-testid="post-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} /></Field>
          <Field label="Excerpt (short summary)"><textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} rows={2} className={inputCls} /></Field>
          <Field label="Cover Image URL"><input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} className={inputCls} placeholder="https://..." /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Category"><input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} /></Field>
            <Field label="Tags (comma separated)"><input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className={inputCls} placeholder="AI, Leadership" /></Field>
          </div>
          <Field label="Content (Markdown — use ## headings, **bold**, > quotes, ![alt](image-url))">
            <textarea data-testid="post-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={14} className={`${inputCls} font-mono text-sm`} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} /> Published
          </label>
          <div className="flex gap-3 pt-2">
            <button onClick={save} disabled={saving} data-testid="save-post" className="flex items-center gap-2 px-6 py-3 btn-red rounded-full font-semibold disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save
            </button>
            <button onClick={() => setEditing(null)} className="px-6 py-3 glass rounded-full font-semibold">Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button onClick={startNew} data-testid="new-post-button" className="flex items-center gap-2 px-5 py-3 btn-red rounded-full font-semibold mb-6"><Plus className="w-4 h-4" /> New Post</button>
      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.id} className="glass-strong rounded-xl p-4 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="font-semibold truncate">{p.title}</p>
              <p className="text-xs text-gray-500">{p.category} · {p.published_at} · {p.is_published ? 'Published' : 'Draft'}</p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => startEdit(p)} className="w-9 h-9 flex items-center justify-center glass rounded-full hover:border-[#EF5B5B] border border-transparent"><Edit className="w-4 h-4" /></button>
              <button onClick={() => del(p)} className="w-9 h-9 flex items-center justify-center glass rounded-full hover:text-[#EF5B5B]"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {posts.length === 0 && <p className="text-gray-500">No posts yet. Create your first reflection!</p>}
      </div>
    </div>
  );
};

// ---------------- Episodes Manager ----------------
const EpisodesManager = () => {
  const [episodes, setEpisodes] = useState([]);
  const [syncing, setSyncing] = useState('');

  const load = () => api.get('/episodes?type=video').then((res) => setEpisodes(res.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const syncApple = async () => {
    setSyncing('apple');
    try {
      const res = await api.post('/episodes/sync-apple');
      toast.success(`Apple sync: +${res.data.added} added, ${res.data.updated} updated`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSyncing(''); }
  };

  const syncYoutube = async () => {
    setSyncing('youtube');
    try {
      const res = await api.post('/episodes/sync');
      toast.success(`YouTube sync: +${res.data.added} added, ${res.data.updated} updated`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSyncing(''); }
  };

  const saveCategories = async (ep, tagsStr) => {
    const categories = tagsStr.split(',').map((t) => t.trim()).filter(Boolean);
    try {
      await api.put(`/episodes/${ep.id}`, { ...ep, categories });
      toast.success('Categories saved');
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const [reflectEp, setReflectEp] = useState(null);
  const [reflectText, setReflectText] = useState('');
  const [savingReflect, setSavingReflect] = useState(false);

  const openReflect = (ep) => { setReflectEp(ep); setReflectText(ep.reflection || ''); };
  const saveReflection = async () => {
    setSavingReflect(true);
    try {
      await api.put(`/episodes/${reflectEp.id}`, { ...reflectEp, reflection: reflectText });
      toast.success('Reflection saved');
      setReflectEp(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingReflect(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-6">
        <button onClick={syncApple} disabled={!!syncing} data-testid="sync-apple" className="flex items-center gap-2 px-5 py-3 btn-red rounded-full font-semibold disabled:opacity-60">
          {syncing === 'apple' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sync from Apple Podcasts
        </button>
        <button onClick={syncYoutube} disabled={!!syncing} data-testid="sync-youtube" className="flex items-center gap-2 px-5 py-3 glass border border-[#EF5B5B]/30 rounded-full font-semibold disabled:opacity-60">
          {syncing === 'youtube' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Youtube className="w-4 h-4" />} Sync YouTube Videos
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-6">{episodes.length} episodes. Add categories (Enter to save) or write the Host's Reflection for each episode.</p>
      <div className="space-y-3">
        {episodes.map((ep) => (
          <div key={ep.id} className="glass-strong rounded-xl p-4">
            <div className="flex items-center gap-4">
              <img src={ep.cover_art} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{ep.title}</p>
                <input
                  defaultValue={(ep.categories || []).join(', ')}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveCategories(ep, e.target.value); }}
                  placeholder="Add categories, press Enter"
                  className="mt-1 w-full bg-transparent border-b border-white/10 text-xs text-gray-400 focus:outline-none focus:border-[#EF5B5B] py-1"
                />
              </div>
              <button
                onClick={() => (reflectEp?.id === ep.id ? setReflectEp(null) : openReflect(ep))}
                data-testid="edit-reflection"
                className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-semibold smooth ${ep.reflection ? 'bg-[#EF5B5B]/20 text-[#EF5B5B]' : 'glass text-gray-300'}`}
              >
                {ep.reflection ? 'Edit Reflection' : 'Add Reflection'}
              </button>
            </div>
            {reflectEp?.id === ep.id && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <textarea
                  value={reflectText}
                  onChange={(e) => setReflectText(e.target.value)}
                  rows={10}
                  data-testid="reflection-textarea"
                  placeholder="Paste or write the reflection (Markdown supported: ## headings, **bold**, > quotes)"
                  className="w-full px-4 py-3 glass rounded-xl text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#EF5B5B]/50"
                />
                <div className="flex gap-3 mt-3">
                  <button onClick={saveReflection} disabled={savingReflect} data-testid="save-reflection" className="flex items-center gap-2 px-5 py-2.5 btn-red rounded-full text-sm font-semibold disabled:opacity-60">
                    {savingReflect ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save Reflection
                  </button>
                  <button onClick={() => setReflectEp(null)} className="px-5 py-2.5 glass rounded-full text-sm font-semibold">Cancel</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------- About Manager ----------------
const AboutManager = () => {
  const [about, setAbout] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get('/about').then((res) => setAbout(res.data)).catch(() => {}); }, []);
  if (!about) return <p className="text-gray-500">Loading...</p>;

  const save = async () => {
    setSaving(true);
    try { await api.put('/about', about); toast.success('About page updated'); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const updateWhy = (i, key, val) => {
    const wl = [...about.why_listen];
    wl[i] = { ...wl[i], [key]: val };
    setAbout({ ...about, why_listen: wl });
  };

  return (
    <div className="glass-strong rounded-2xl p-6 sm:p-8 max-w-3xl space-y-4" data-testid="about-editor">
      <Field label="Name"><input value={about.name} onChange={(e) => setAbout({ ...about, name: e.target.value })} className={inputCls} /></Field>
      <Field label="Headline"><input value={about.headline} onChange={(e) => setAbout({ ...about, headline: e.target.value })} className={inputCls} /></Field>
      <Field label="Photo URL"><input value={about.photo_url} onChange={(e) => setAbout({ ...about, photo_url: e.target.value })} className={inputCls} placeholder="https://..." /></Field>
      <Field label="Bio (Markdown)"><textarea value={about.bio} onChange={(e) => setAbout({ ...about, bio: e.target.value })} rows={8} className={inputCls} /></Field>
      <div>
        <p className="text-sm text-gray-400 mb-3">Why Listen?</p>
        <div className="space-y-3">
          {(about.why_listen || []).map((w, i) => (
            <div key={i} className="grid grid-cols-1 gap-2 glass rounded-xl p-3">
              <input value={w.title} onChange={(e) => updateWhy(i, 'title', e.target.value)} className={inputCls} placeholder="Title" />
              <input value={w.description} onChange={(e) => updateWhy(i, 'description', e.target.value)} className={inputCls} placeholder="Description" />
            </div>
          ))}
        </div>
      </div>
      <button onClick={save} disabled={saving} data-testid="save-about" className="flex items-center gap-2 px-6 py-3 btn-red rounded-full font-semibold disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save About Page
      </button>
    </div>
  );
};

// ---------------- Speaker Applications Manager ----------------
const STATUS_STYLES = {
  new: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  reviewing: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  approved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  declined: 'bg-red-500/15 text-red-300 border-red-500/30',
};

const ApplicationsManager = () => {
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState(null);

  const load = () => api.get('/speaker-applications').then((res) => setApps(res.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const patch = async (id, body) => {
    setBusy(id);
    try {
      await api.patch(`/speaker-applications/${id}`, body);
      await load();
      toast.success('Updated');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const shown = filter === 'all' ? apps : apps.filter((a) => a.status === filter);
  const counts = apps.reduce((acc, a) => ({ ...acc, [a.status]: (acc[a.status] || 0) + 1 }), {});

  return (
    <div data-testid="applications-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex flex-wrap gap-2">
          {['all', 'new', 'reviewing', 'approved', 'declined'].map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`app-filter-${k}`}
              className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider smooth ${filter === k ? 'bg-[#EF5B5B] text-white' : 'glass text-gray-300 hover:bg-white/10'}`}
            >
              {k}{k !== 'all' && counts[k] ? ` · ${counts[k]}` : ''}
            </button>
          ))}
        </div>
        <p className="text-sm text-gray-400">{apps.length} total application{apps.length !== 1 ? 's' : ''}</p>
      </div>

      {shown.length === 0 ? (
        <div className="text-center py-16 rounded-2xl glass-strong border border-white/8">
          <Mic className="w-8 h-8 text-gray-500 mx-auto mb-3" />
          <p className="text-gray-400">No applications in this bucket yet.</p>
        </div>
      ) : (
        <div className="grid gap-5">
          {shown.map((a) => (
            <div key={a.id} className="glass-strong rounded-2xl p-6 border border-white/8" data-testid={`app-card-${a.id}`}>
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="text-lg font-bold">{a.name}</h3>
                    <span className={`text-[10px] px-2.5 py-1 rounded-full border font-semibold uppercase tracking-wider ${STATUS_STYLES[a.status] || STATUS_STYLES.new}`}>{a.status}</span>
                    {a.featured_publicly && (
                      <span className="text-[10px] px-2.5 py-1 rounded-full bg-[#EF5B5B]/15 text-[#EF5B5B] border border-[#EF5B5B]/30 font-semibold uppercase tracking-wider">
                        Public
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-400 mt-1">
                    <a href={`mailto:${a.email}`} className="hover:text-white">{a.email}</a>
                    {a.expertise && ` · ${a.expertise}`}
                  </p>
                  {a.linkedin_url && (
                    <a href={a.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0A66C2] hover:underline inline-flex items-center gap-1 mt-1">
                      LinkedIn <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                <span className="text-xs text-gray-500 whitespace-nowrap">
                  {new Date(a.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              </div>

              <div className="p-4 rounded-xl bg-black/30 border border-white/5 mb-4">
                <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">Pitch</p>
                <p className="text-sm text-gray-200 whitespace-pre-line leading-relaxed">{a.pitch}</p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {a.status !== 'reviewing' && (
                  <button
                    disabled={busy === a.id}
                    onClick={() => patch(a.id, { status: 'reviewing' })}
                    data-testid={`app-mark-reviewing-${a.id}`}
                    className="px-4 py-2 rounded-full text-xs font-semibold glass border border-white/10 hover:border-amber-400 hover:text-amber-300 smooth"
                  >
                    Mark reviewing
                  </button>
                )}
                {a.status !== 'approved' && (
                  <button
                    disabled={busy === a.id}
                    onClick={() => patch(a.id, { status: 'approved' })}
                    data-testid={`app-approve-${a.id}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 smooth"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Approve &amp; email
                  </button>
                )}
                {a.status !== 'declined' && (
                  <button
                    disabled={busy === a.id}
                    onClick={() => patch(a.id, { status: 'declined' })}
                    data-testid={`app-decline-${a.id}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-red-500/15 text-red-300 border border-red-500/30 hover:bg-red-500/25 smooth"
                  >
                    <XCircle className="w-3.5 h-3.5" /> Decline &amp; email
                  </button>
                )}
                {a.status === 'approved' && (
                  <button
                    disabled={busy === a.id}
                    onClick={() => patch(a.id, { featured_publicly: !a.featured_publicly })}
                    data-testid={`app-feature-${a.id}`}
                    className={`px-4 py-2 rounded-full text-xs font-semibold border smooth ${a.featured_publicly ? 'bg-[#EF5B5B]/15 text-[#EF5B5B] border-[#EF5B5B]/40' : 'glass border-white/10 hover:border-[#EF5B5B]/50 hover:text-[#EF5B5B]'}`}
                  >
                    {a.featured_publicly ? 'Featured publicly · click to hide' : 'Feature on Pitch Gallery'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------- Subscribers Manager ----------------
const SubscribersManager = () => {
  const [subs, setSubs] = useState([]);
  useEffect(() => { api.get('/subscribers').then((res) => setSubs(res.data)).catch(() => {}); }, []);
  return (
    <div>
      <p className="text-lg font-semibold mb-4">{subs.length} subscribers</p>
      <div className="glass-strong rounded-2xl overflow-hidden">
        {subs.map((s) => (
          <div key={s.id} className="flex items-center justify-between px-5 py-3 border-b border-white/5">
            <span className="text-sm">{s.email}</span>
            <span className="text-xs text-gray-500">{(s.subscribed_at || '').slice(0, 10)}</span>
          </div>
        ))}
        {subs.length === 0 && <p className="text-gray-500 p-5">No subscribers yet.</p>}
      </div>
    </div>
  );
};

const inputCls = 'w-full px-4 py-2.5 glass rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#EF5B5B]/50';
const Field = ({ label, children }) => (
  <div><label className="block text-sm text-gray-400 mb-2">{label}</label>{children}</div>
);

export default AdminDashboard;
